import { useState, useEffect, useCallback, useRef } from "react";
import { InfoClient, SubscriptionClient } from "../sdk";
import { ConnectionState } from "../sdk/types/transport";

/**
 * Retry a function with exponential backoff
 * @param fn - Function to retry
 * @param maxRetries - Maximum number of retries (default: 3)
 * @param initialDelay - Initial delay in ms (default: 1000)
 * @returns Promise that resolves with the function result
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000,
): Promise<T> {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      if (attempt < maxRetries) {
        const delay = initialDelay * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError || new Error("Retry failed");
}

export interface FundingRateConfig {
  baseURL: string;
  wsURL: string;
  symbols: string[]; // Support multiple symbols
  historyDepth?: number; // Number of historical updates to retain (default: 1 for latest only)
  authToken?: string;
  walletAddress?: string;
  deviceID?: string;
}

export interface FundingRateData {
  symbol: string;
  market_id: number;
  market_type: string;
  funding_rate: string;
  mark_price: string;
  index_price: string;
  premium_rate: string;
  ema_funding_rate: string;
  raw_funding_rate: string;
  calculation_time_unix: number;
  next_funding_time_unix: number;
  funding_interval_seconds: number;
  calculation_method: string;
  is_valid: boolean;
  is_stale: boolean;
  calculation_details?: Record<string, string>;
  // Options-specific (optional):
  option_type?: string;
  strike_price?: string;
  mark_iv?: string;
  time_value?: string;
  intrinsic_value?: string;
  funding_premium?: string;
}

/**
 * Hook for managing funding rate data with HTTP initialization and WebSocket real-time updates
 * Supports multiple symbols with configurable history retention
 */
export function useFundingRate(config: FundingRateConfig) {
  const historyDepth = config.historyDepth ?? 1; // Default: latest only

  // Store latest funding rate per symbol (or history array if historyDepth > 1)
  const [fundingRates, setFundingRates] = useState<
    Map<string, FundingRateData | FundingRateData[]>
  >(new Map());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);

  const infoClientRef = useRef<InfoClient | null>(null);
  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const isSubscribedRef = useRef<Set<string>>(new Set()); // Track subscribed symbols

  /**
   * Initialize funding rate from HTTP request with retry logic
   */
  const initializeFundingRate = useCallback(
    async (symbol: string) => {
      if (!infoClientRef.current) return;

      setError(null);

      try {
        const data = await retryWithBackoff(
          () => infoClientRef.current!.getFundingRate(symbol, 1),
          3,
          1000,
        );
        // API may return array or single object
        const rateData = Array.isArray(data) ? data[0] : data;

        if (rateData) {
          setFundingRates((prev) => {
            const updated = new Map(prev);
            if (historyDepth === 1) {
              updated.set(symbol, rateData as FundingRateData);
            } else {
              updated.set(symbol, [rateData as FundingRateData]);
            }
            return updated;
          });
        }
      } catch (err) {
        console.error(`Failed to initialize funding rate for ${symbol} after retries:`, err);
        // Don't set error state for individual symbol failures, but log for debugging
      }
    },
    [historyDepth],
  );

  /**
   * Handle funding rate update from WebSocket
   */
  const handleFundingRateUpdate = useCallback(
    (symbol: string, data: any) => {
      if (!data || !data.symbol) return;

      setFundingRates((prev) => {
        const updated = new Map(prev);
        const current = updated.get(symbol);

        if (historyDepth === 1) {
          // Store only latest
          updated.set(symbol, data as FundingRateData);
        } else {
          // Store history array
          const history = Array.isArray(current)
            ? current
            : current
              ? [current as FundingRateData]
              : [];
          const newHistory = [data as FundingRateData, ...history].slice(
            0,
            historyDepth,
          );
          updated.set(symbol, newHistory);
        }

        return updated;
      });
    },
    [historyDepth],
  );

  // Initialize clients and subscribe
  useEffect(() => {
    const infoClient = new InfoClient({
      baseURL: config.baseURL,
    });
    infoClientRef.current = infoClient;

    const subscriptionClient = new SubscriptionClient({
      url: config.wsURL,
      authToken: config.authToken,
      walletAddress: config.walletAddress,
      deviceID: config.deviceID,
    });
    subscriptionClientRef.current = subscriptionClient;

    // Initialize funding rates from HTTP
    setLoading(true);
    Promise.all(
      config.symbols.map((symbol) => initializeFundingRate(symbol)),
    ).finally(() => {
      setLoading(false);
    });

    // Subscribe function that can be called on initial connect or reconnection
    const subscribe = () => {
      if (
        subscriptionClient.isConnected() &&
        subscriptionClientRef.current
      ) {
        config.symbols.forEach((symbol) => {
          if (!isSubscribedRef.current.has(symbol)) {
            const callback = (data: any) => {
              handleFundingRateUpdate(symbol, data);
            };
            subscriptionClient.subscribeFundingRate(symbol, callback);
            isSubscribedRef.current.add(symbol);
          }
        });
      }
    };

    // Connect WebSocket and subscribe
    subscriptionClient
      .connect()
      .then(() => {
        setIsConnected(true);
        subscribe();
      })
      .catch((err) => {
        console.error("Failed to connect WebSocket:", err);
        setError("WebSocket connection failed");
      });

    // Monitor connection state changes and re-subscribe on reconnection
    let unsubscribeStateChange: (() => void) | null = null;
    const wsClient = (subscriptionClient as any).wsClient;
    if (wsClient) {
      unsubscribeStateChange = wsClient.onStateChange(
        (state: ConnectionState) => {
          if (state === ConnectionState.Connected) {
            setIsConnected(true);
            // Re-subscribe if we were previously subscribed
            if (isSubscribedRef.current.size > 0) {
              subscribe();
            }
          } else if (
            state === ConnectionState.Disconnected ||
            state === ConnectionState.Reconnecting
          ) {
            setIsConnected(false);
            // Clear subscription tracking on disconnect
            isSubscribedRef.current.clear();
          }
        },
      );
    }

    return () => {
      // Unsubscribe from state changes
      if (unsubscribeStateChange) {
        unsubscribeStateChange();
      }

      if (subscriptionClientRef.current) {
        // Unsubscribe from all symbols
        config.symbols.forEach((symbol) => {
          try {
            subscriptionClientRef.current?.unsubscribeFundingRate(symbol);
          } catch (err) {
            console.error(
              `Failed to unsubscribe from funding rate for ${symbol}:`,
              err,
            );
          }
        });
        subscriptionClientRef.current.disconnect();
      }
    };
  }, [
    config.baseURL,
    config.wsURL,
    config.symbols.join(","), // Use join to track symbol changes
    config.authToken,
    config.walletAddress,
    config.deviceID,
    initializeFundingRate,
    handleFundingRateUpdate,
  ]);

  // Re-subscribe when symbols change
  useEffect(() => {
    if (
      subscriptionClientRef.current?.isConnected() &&
      subscriptionClientRef.current
    ) {
      // Unsubscribe from symbols no longer in config
      const currentSymbols = new Set(config.symbols);
      isSubscribedRef.current.forEach((symbol) => {
        if (!currentSymbols.has(symbol)) {
          subscriptionClientRef.current?.unsubscribeFundingRate(symbol);
          isSubscribedRef.current.delete(symbol);
        }
      });

      // Subscribe to new symbols
      config.symbols.forEach((symbol) => {
        if (!isSubscribedRef.current.has(symbol)) {
          const callback = (data: any) => {
            handleFundingRateUpdate(symbol, data);
          };
          subscriptionClientRef.current?.subscribeFundingRate(
            symbol,
            callback,
          );
          isSubscribedRef.current.add(symbol);
        }
      });
    }
  }, [config.symbols.join(","), handleFundingRateUpdate]);

  /**
   * Get funding rate for a specific symbol
   */
  const getFundingRate = useCallback(
    (symbol: string): FundingRateData | FundingRateData[] | undefined => {
      return fundingRates.get(symbol);
    },
    [fundingRates],
  );

  /**
   * Get latest funding rate for a symbol (convenience method)
   */
  const getLatestFundingRate = useCallback(
    (symbol: string): FundingRateData | undefined => {
      const data = fundingRates.get(symbol);
      if (!data) return undefined;
      return Array.isArray(data) ? data[0] : (data as FundingRateData);
    },
    [fundingRates],
  );

  /**
   * Refresh funding rate for a specific symbol
   */
  const refresh = useCallback(
    async (symbol?: string) => {
      if (symbol) {
        await initializeFundingRate(symbol);
      } else {
        setLoading(true);
        await Promise.all(
          config.symbols.map((s) => initializeFundingRate(s)),
        );
        setLoading(false);
      }
    },
    [config.symbols, initializeFundingRate],
  );

  return {
    fundingRates,
    getFundingRate,
    getLatestFundingRate,
    loading,
    error,
    isConnected,
    refresh,
  };
}
